// @flow

import React from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import actionCreators from "@modules/shared/actionCreator";
import { GSGrid, Icons } from "@components";
import {
  selectEmployeePositionsList,
  getSelectedEmployeePosition,
} from "@modules/shared/selectors";
import type { State } from "@store/reducers";
import type { RoleSummaryType } from "@modules/employee/types";
import { formatDate } from "@transformers/date";

type OwnProps = $Exact<{
  clientId: number,
  employeeId: number,
  currentStep: number,
  handleSubmit: Function, // Prop added for LinearWizard purposes
  editable: boolean, // Prop added for LinearWizard purposes
}>;

type PropsFromState = $Exact<{
  employeePositionsList: {
    content: Array<RoleSummaryType>,
  },
  selectedEmployeePosition: RoleSummaryType | null,
}>;

type PropsFromDispatch = $Exact<{
  getEmployeePositionsList: typeof actionCreators.GetEmployeePositionsList,
  selectEmployeePosition: typeof actionCreators.SelectEmployeePosition,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export class SelectPositionToAssignShiftPatternToStep extends React.Component<Props> {
  componentDidUpdate(props: Object) {
    const { clientId, employeeId } = this.props;
    if (clientId && employeeId && this.props.employeeId !== props.employeeId) {
      this.props.getEmployeePositionsList({
        clientId,
        employeeId,
      });
    }
  }

  selectEmployeePosition = (position: RoleSummaryType) => () => {
    this.props.selectEmployeePosition(position);
    this.props.handleSubmit(position, true);
  };

  get columns() {
    const columnsConfig = [
      {
        key: "id",
        name: this.props.t("POSITION_ID"),
      },
      {
        key: "employeePositionReference",
        name: this.props.t("POSITION_REF"),
      },
      {
        key: "jobTitle",
        name: this.props.t("JOB_TITLE"),
      },
      {
        key: "staffGroup",
        name: this.props.t("STAFF_GROUP"),
        renderCell: ({ row }: { row: RoleSummaryType }) => {
          return row.staffGroup ? row.staffGroup.name : this.props.t("N/A");
        },
      },
      {
        key: "department",
        name: this.props.t("DEPARTMENT"),
        renderCell: ({ row }: { row: RoleSummaryType }) => {
          return row.organisationalUnit ? row.organisationalUnit.name : this.props.t("N/A");
        },
      },
      {
        key: "activeTo",
        name: this.props.t("EFFECTIVE_END_DATE"),
        renderCell: ({ row }: { row: RoleSummaryType }) => {
          return row.activeTo ? formatDate(row.activeTo) : this.props.t("N/A");
        },
      },
    ];
    if (this.props.editable)
      columnsConfig.push({
        name: this.props.t("SELECT"),
        key: "actions",
        renderCell: ({ row }: { row: RoleSummaryType }) => {
          return (
            <div
              className="d-flex align-items-center pointer"
              onClick={this.selectEmployeePosition(row)}
            >
              <span>{this.props.t("SELECT")}</span>
              <Icons.ArrowRightIcon className="ml-1" />
            </div>
          );
        },
      });
    return columnsConfig;
  }
  get rowData() {
    return (this.props.employeePositionsList || {}).content || [];
  }

  render() {
    if (!this.props.selectedEmployeePosition && this.props.employeePositionsList.content.length < 1)
      return <div />;
    if (!this.props.editable && this.props.selectedEmployeePosition)
      return <GSGrid rows={[this.props.selectedEmployeePosition]} columns={this.columns} />;

    return <GSGrid rows={this.rowData} columns={this.columns} />;
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  employeePositionsList: selectEmployeePositionsList(state),
  selectedEmployeePosition: getSelectedEmployeePosition(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getEmployeePositionsList: actionCreators.GetEmployeePositionsList,
  selectEmployeePosition: actionCreators.SelectEmployeePosition,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(localize()(SelectPositionToAssignShiftPatternToStep));
